import { buttonVariants } from "@postdaily/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { trackOncePerTab } from "@/lib/analytics";
import { pageHead } from "@/lib/seo";

const successSearchSchema = z.object({
  checkout_id: z.string().optional(),
});

export const Route = createFileRoute("/(public)/success")({
  validateSearch: successSearchSchema.parse,
  head: () => pageHead("success"),
  component: SuccessPage,
});

function SuccessPage() {
  const { checkout_id } = Route.useSearch();

  // Conversion event: the user has returned from Polar with an active checkout.
  // Once per checkout, so refreshing this page does not count it again.
  useEffect(() => {
    trackOncePerTab(
      `subscription_activated:${checkout_id ?? "unknown"}`,
      "subscription_activated",
      { checkout_id: checkout_id ?? null },
    );
  }, [checkout_id]);

  return (
    <div className="flex min-h-svh items-center justify-center bg-background px-6 py-16">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl border bg-card p-8 text-center shadow-sm">
        <span className="flex size-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
          <CheckCircle2 className="size-8" />
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="font-semibold text-2xl tracking-tight">
            You're all set
          </h1>
          <p className="text-muted-foreground text-sm">
            Your subscription is active. It can take a moment to appear in the
            app — refresh your billing page if you don't see it right away.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2">
          <Link to="/queue" className={buttonVariants({ className: "w-full" })}>
            Go to your queue
            <ArrowRight data-icon="inline-end" />
          </Link>
          <Link
            to="/settings/billing"
            className={buttonVariants({
              variant: "outline",
              className: "w-full",
            })}
          >
            View billing
          </Link>
        </div>
      </div>
    </div>
  );
}
